// https://dev-api.yester.eu/api/swagger-ui/#/hr

import ApiClient from '@/api/ApiClient';
import * as api from '@/views/modules/hr/api/types';
import { FilterResponse } from '@/views/modules/utils/types';

const API_PATH = '/api/hr';
const WORK_API_PATH = `${API_PATH}/work`;
const HEALTH_API_PATH = `${API_PATH}/health`;
const PROFILE_API_PATH = `${API_PATH}/user/profile`;
const FAMILY_API_PATH = `${API_PATH}/users/family-members`;
const CLOTHES_API_PATH = `${API_PATH}/users/work-clothes`;

export default class Client {
  apiClient: ApiClient;

  constructor(apiClient?: ApiClient) {
    this.apiClient = apiClient || ApiClient.instance;
  }

  getUser(userId: string): Promise<api.User> {
    if (!userId) {
      throw new Error("Error: Missing the required parameter 'userId' when calling getUser.");
    }

    return this.apiClient.get({ path: `${PROFILE_API_PATH}/${userId}`, params: null });
  }

  upsertUser(userId: string, body: api.User): Promise<api.User> {
    if (!userId || !body) {
      throw new Error("Error: Missing the required parameter 'userId' or 'body' when calling updateUser.");
    }

    return this.apiClient.put({ path: `${API_PATH}/users/${userId}`, body });
  }

  getAvatar(userId: string) {
    if (!userId) {
      throw new Error("Error: Missing the required parameter 'userId' when calling getAvatar.");
    }

    return this.apiClient.get({ path: `${PROFILE_API_PATH}/documents/${userId}/photo`, params: null });
  }

  uploadAvatar(userId: string, body: object) {
    if (!userId || !body) {
      throw new Error("Error: Missing the required parameter 'userId' or 'file' when calling uploadAvatar.");
    }

    return this.apiClient.post({ path: `${PROFILE_API_PATH}/documents/${userId}/photo`, body });
  }

  // list HR users with optional filter and pagination.
  async listUsers(filter: string, params: api.GetUserParams): Promise<FilterResponse<api.User>> {
    if (!params) {
      throw new Error("Error: Missing the required parameter 'params' when calling list.");
    }

    return this.apiClient.searchWithParamsFilters({ path: `${API_PATH}/users`, filter, params });
  }

  getStatistics(request: api.GetStatisticsRequest): Promise<api.GetStatisticsResponse> {
    const { filter, ...params } = request;
    return this.apiClient.searchWithParamsFilters({
      path: `${API_PATH}/users/statistics`,
      filter,
      params,
    });
  }

  // user personal information API.
  listPersonalInfo(userId: string): Promise<api.Contacts> {
    if (!userId) {
      throw new Error("Error: Missing the required parameter 'userId' when calling listPersonalInfo.");
    }

    return this.apiClient.get({ path: `${PROFILE_API_PATH}/personal-information/${userId}`, params: null });
  }

  upsertPersonalInfo(body: api.PersonalInformation): Promise<api.PersonalInformation> {
    return this.apiClient.post({ path: `${PROFILE_API_PATH}/personal-information`, body });
  }

  // user contacts information API.
  listContactInfo(userId: string): Promise<api.PersonalInformation> {
    if (!userId) {
      throw new Error("Error: Missing the required parameter 'userId' when calling listContactInfo.");
    }

    return this.apiClient.get({ path: `${PROFILE_API_PATH}/contacts/${userId}`, params: null });
  }

  upsertContactInfo(body: api.Contacts): Promise<api.Contacts> {
    return this.apiClient.post({ path: `${PROFILE_API_PATH}/contacts`, body });
  }

  // user official information API.
  listOfficialInfo(userId: string): Promise<api.OfficialInformation> {
    if (!userId) {
      throw new Error("Error: Missing the required parameter 'userId' when calling listOfficialInfo.");
    }

    return this.apiClient.get({ path: `${PROFILE_API_PATH}/official-information/${userId}`, params: null });
  }

  upsertOfficialInfo(body: api.OfficialInformation): Promise<api.OfficialInformation> {
    return this.apiClient.post({ path: `${PROFILE_API_PATH}/official-information`, body });
  }

  // user financial information API.
  listFinancialInfo(userId: string): Promise<api.FinancialInformation> {
    if (!userId) {
      throw new Error("Error: Missing the required parameter 'userId' when calling listFinancialInfo.");
    }

    return this.apiClient.get({ path: `${PROFILE_API_PATH}/financial-information/${userId}`, params: null });
  }

  upsertFinancialInfo(body: api.FinancialInformation): Promise<api.FinancialInformation> {
    return this.apiClient.post({ path: `${PROFILE_API_PATH}/financial-information`, body });
  }

  // user work cloths sizes API.
  listWorkClothsSizes(userId: string): Promise<api.WorkClothingSize> {
    if (!userId) {
      throw new Error("Error: Missing the required parameter 'userId' when calling listWorkClothsSizes.");
    }

    return this.apiClient.get({ path: `${PROFILE_API_PATH}/work-cloth-info/${userId}`, params: null });
  }

  upsertWorkClothsSizes(body: api.WorkClothingSize): Promise<api.WorkClothingSize> {
    return this.apiClient.post({ path: `${PROFILE_API_PATH}/work-cloth-info`, body });
  }

  // user emergency contact API.
  listEmergencyContact(userId: string): Promise<api.EmergencyContact> {
    if (!userId) {
      throw new Error("Error: Missing the required parameter 'userId' when calling listEmergencyContact.");
    }

    return this.apiClient.get({ path: `${PROFILE_API_PATH}/emergency-contacts/${userId}`, params: null });
  }

  upsertEmergencyContact(body: api.EmergencyContact): Promise<api.EmergencyContact> {
    return this.apiClient.post({ path: `${PROFILE_API_PATH}/emergency-contacts`, body });
  }

  // user tax information API.
  listTaxInfo(userId: string): Promise<api.TaxInformation> {
    if (!userId) {
      throw new Error("Error: Missing the required parameter 'userId' when calling listTaxInfo.");
    }

    return this.apiClient.get({ path: `${PROFILE_API_PATH}/tax-information/${userId}`, params: null });
  }

  upsertTaxInfo(body: api.TaxInformation): Promise<api.TaxInformation> {
    return this.apiClient.post({ path: `${PROFILE_API_PATH}/tax-information`, body });
  }

  // user family information API.
  listFamily(userId: string): Promise<api.Family> {
    if (!userId) {
      throw new Error("Error: Missing the required parameter 'userId' when calling listFamily.");
    }

    return this.apiClient.get({ path: `${PROFILE_API_PATH}/family/${userId}`, params: null });
  }

  upsertFamily(body: api.Family): Promise<api.Family> {
    return this.apiClient.post({ path: `${PROFILE_API_PATH}/family`, body });
  }

  listWorks(filter: string, params: api.GetUserParams): Promise<FilterResponse<api.UserContractListItem>> {
    if (!params) {
      throw new Error("Error: Missing the required parameter 'params' when calling list.");
    }

    return this.apiClient.searchWithParamsFilters({
      path: `${API_PATH}/works`,
      filter,
      params,
    });
  }

  getWorksStatistics(request: api.GetStatisticsRequest): Promise<api.GetStatisticsResponse> {
    const { filter, ...params } = request;
    return this.apiClient.searchWithParamsFilters({
      path: `${API_PATH}/works/statistics`,
      filter,
      params,
    });
  }

  // upsert HR contract.
  upsertContract(body: api.Contract): Promise<api.Contract> {
    if (!body) {
      throw new Error("Error: Missing the required parameter 'body' when calling upsertContract.");
    }

    return this.apiClient.post({ path: `${WORK_API_PATH}/contracts/`, body });
  }

  // get HR contract by id.
  getContract(contractId: string): Promise<api.Contract> {
    if (!contractId) {
      throw new Error("Error: Missing the required parameter 'contractId' when calling getContract.");
    }

    return this.apiClient.get({ path: `${WORK_API_PATH}/${contractId}`, params: null });
  }

  listPositions(contactId: string): Promise<api.Position[]> {
    if (!contactId) {
      throw new Error("Error: Missing the required parameter 'contactId' when calling listPositions.");
    }

    return this.apiClient.get({ path: `${WORK_API_PATH}/positions/${contactId}`, params: null });
  }

  upsertPositions(contractId: string, body: api.Position[]): Promise<api.Position> {
    if (!contractId && !body) {
      throw new Error("Error: Missing the required parameter 'contractId' or 'body' when calling upsertPosition.");
    }

    return this.apiClient.post({ path: `${WORK_API_PATH}/positions/${contractId}`, body });
  }

  listCompensations(contactId: string): Promise<api.Compensation[]> {
    if (!contactId) {
      throw new Error("Error: Missing the required parameter 'contactId' when calling listCompensations.");
    }

    return this.apiClient.get({ path: `${WORK_API_PATH}/compensation/${contactId}`, params: null });
  }

  upsertCompensations(contractId: string, body: api.Compensation[]): Promise<api.Compensation[]> {
    if (!contractId && !body) {
      throw new Error("Error: Missing the required parameter 'body' when calling upsertCompensation.");
    }

    return this.apiClient.post({ path: `${WORK_API_PATH}/compensation/${contractId}`, body });
  }

  listDimensions(contactId: string): Promise<api.Dimension[]> {
    if (!contactId) {
      throw new Error("Error: Missing the required parameter 'contactId' when calling listDimensions.");
    }

    return this.apiClient.get({ path: `${WORK_API_PATH}/dimensions/${contactId}`, params: null });
  }

  saveOrUpdateContractDimensions({ contractId, body }: { contractId: number; body: api.ContractDimensionsDTO; }): Promise<api.ContractDimensionsDTO> {
    if (!contractId && !body) {
      throw new Error("Error: Missing the required parameter 'body' when calling upsertDimensions.");
    }

    return this.apiClient.post({ path: `${WORK_API_PATH}/dimensions`, body });
  }

  getContractDimensions({ contractId }: { contractId: number; }): Promise<api.ContractDimensionsDTO> {
    return this.apiClient.get({ path: `${WORK_API_PATH}/dimensions/${contractId}` });
  }

  listLeaveBalances(contactId: string): Promise<api.Dimension[]> {
    if (!contactId) {
      throw new Error("Error: Missing the required parameter 'contactId' when calling listLeaveBalances.");
    }

    return this.apiClient.get({ path: `${WORK_API_PATH}/leave-balances/${contactId}`, params: null });
  }

  upsertLeaveBalances(contractId: string, body: api.Dimension[]): Promise<api.Dimension[]> {
    if (!contractId && !body) {
      throw new Error("Error: Missing the required parameter 'body' when calling upsertLeaveBalances.");
    }

    return this.apiClient.post({ path: `${WORK_API_PATH}/leave-balances/${contractId}`, body });
  }

  // Health API
  listHealthChecks(filter: string, params: api.GetUserParams): Promise<FilterResponse<api.UserContractListItem>> {
    if (!params) {
      throw new Error("Error: Missing the required parameter 'params' when calling listHealthChecks.");
    }

    return this.apiClient.searchWithParamsFilters({
      path: `${API_PATH}/health`,
      filter,
      params,
    });
  }

  getHealthCheck(healthId: string): Promise<api.HealthCheck[]> {
    if (!healthId) {
      throw new Error("Error: Missing the required parameter 'healthId' when calling getHealthCheck.");
    }

    return this.apiClient.get({ path: `${HEALTH_API_PATH}/${healthId}`, params: null });
  }

  upsertHealthCheck(body: api.HealthCheck): Promise<api.HealthCheck> {
    if (!body) {
      throw new Error("Error: Missing the required parameter 'body' when calling upsertHealthCheck.");
    }

    return this.apiClient.post({ path: `${HEALTH_API_PATH}`, body });
  }

  getHealthStatistics(request: api.GetStatisticsRequest): Promise<api.GetStatisticsResponse> {
    const { filter, ...params } = request;

    return this.apiClient.searchWithParamsFilters({
      path: `${HEALTH_API_PATH}/statistics`,
      filter,
      params,
    });
  }

  // list HR family data with optional filter and pagination.
  // @ts-ignore
  async listFamilyMembers(filter: string, params: api.GetFamilyParams): Promise {
    if (!params) {
      throw new Error("Error: Missing the required parameter 'params' when calling list.");
    }

    return this.apiClient.searchWithParamsFilters({ path: `${FAMILY_API_PATH}`, filter, params });
  }

  getFamilyMembersStatistics(request: api.GetStatisticsRequest): Promise<api.GetStatisticsResponse> {
    const { filter, ...params } = request;

    return this.apiClient.searchWithParamsFilters({
      path: `${FAMILY_API_PATH}/statistics`,
      filter,
      params,
    });
  }

  // @ts-ignore
  async listWorkClothes(filter: string, params: api.GetFamilyParams) {
    if (!params) {
      throw new Error("Error: Missing the required parameter 'params' when calling list.");
    }

    return this.apiClient.searchWithParamsFilters({ path: `${CLOTHES_API_PATH}`, filter, params });
  }

  getWorkClothesStatistics(request: api.GetStatisticsRequest): Promise<api.GetStatisticsResponse> {
    const { filter, ...params } = request;

    return this.apiClient.searchWithParamsFilters({
      path: `${CLOTHES_API_PATH}/statistics`,
      filter,
      params,
    });
  }
}
